import { Alert, Button, Modal } from '@ashniva/ui';
import { useState } from 'react';

import { useReauth } from '../../auth/reauth';
import { useRemoveRegistration, useRequestRegistration } from '../api';
import { classifyProblem } from '../ri-problems';
import type { RepositoryState } from '../ri-state';
import { useSubmissionKey } from '../use-submission-key';
import { ProblemNotice } from './ProblemNotice';

interface RegistrationActionsProps {
  linkId: string;
  state: RepositoryState;
  /** Called once a request or a removal went through, to follow what happens next. */
  onChanged: () => void;
}

/**
 * Request indexing, cancel a request, or take the repository out of Repository Intelligence (P14-03). For people
 * with `repository-intelligence:manage` only, and each asks for the password again, as the API does.
 */
export function RegistrationActions({ linkId, state, onChanged }: RegistrationActionsProps) {
  const reauth = useReauth();
  const request = useRequestRegistration(linkId);
  const remove = useRemoveRegistration(linkId);
  const requestKeys = useSubmissionKey();
  const removeKeys = useSubmissionKey();
  const [confirming, setConfirming] = useState(false);
  const busy = request.isPending || remove.isPending || reauth.active;

  const canRequest = ['not-registered', 'rejected'].includes(state.kind);
  const inFlight = ['pending', 'provisioning', 'indexing'].includes(state.kind);

  const onRequest = async () => {
    if (busy) return;
    const token = await reauth.request().catch(() => null);
    if (!token) return;
    const clientRequestId = requestKeys.start('register');
    if (!clientRequestId) return;
    request.mutate(
      { headers: reauth.headers(token), clientRequestId },
      requestKeys.callbacks(onChanged),
    );
  };

  const onRemove = async () => {
    setConfirming(false);
    if (busy) return;
    const token = await reauth.request().catch(() => null);
    if (!token) return;
    const clientRequestId = removeKeys.start('remove');
    if (!clientRequestId) return;
    remove.mutate(
      { headers: reauth.headers(token), clientRequestId },
      removeKeys.callbacks(onChanged),
    );
  };

  return (
    <>
      {canRequest ? (
        <Button
          variant="primary"
          loading={request.isPending}
          disabled={busy}
          onClick={() => void onRequest()}
        >
          Request indexing
        </Button>
      ) : (
        <Button
          variant="danger"
          loading={remove.isPending}
          disabled={busy}
          onClick={() => setConfirming(true)}
        >
          {inFlight ? 'Cancel the request' : 'Remove from Repository Intelligence'}
        </Button>
      )}
      {request.isError && classifyProblem(request.error) === 'conflict' ? (
        <Alert tone="info">
          This repository is already registered. Its progress is shown here.
        </Alert>
      ) : null}
      {request.isError && classifyProblem(request.error) !== 'conflict' ? (
        <ProblemNotice error={request.error} size="sm" />
      ) : null}
      {remove.isError ? <ProblemNotice error={remove.error} size="sm" /> : null}
      {remove.isSuccess ? (
        <Alert tone="success">
          {remove.data.outcome === 'cancelled'
            ? 'The request was cancelled.'
            : 'Repository Intelligence stopped serving this repository. Its copy is deleted after the retention period.'}
        </Alert>
      ) : null}
      <Modal
        open={confirming && !reauth.active}
        size="sm"
        title={
          inFlight ? 'Cancel the request?' : 'Remove this repository from Repository Intelligence?'
        }
        onClose={() => setConfirming(false)}
        footer={
          <>
            <Button onClick={() => setConfirming(false)}>Keep it</Button>
            <Button variant="danger" onClick={() => void onRemove()}>
              {inFlight ? 'Cancel the request' : 'Remove'}
            </Button>
          </>
        }
      >
        <p>
          {inFlight
            ? 'Repository Intelligence will not index this repository. You can request it again later.'
            : 'Search, files, questions and analyses stop working for it. Repository Intelligence deletes its copy after its retention period; requesting it again starts over.'}
        </p>
      </Modal>
      {reauth.modal}
    </>
  );
}
